import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniOtpInput } from '@/design-system/components/inputs';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import { isEmpty, isString } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { PageHeaderModalNative } from '@/features/bill-payment/screens/components/PageHeaderModalNative';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { OtpInput, UnionType } from '@/features/bill-payment/enum';
import { useInstructions } from '@/features/bill-payment/hooks/useUnionPay.hook';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { LayoutChangeEvent } from 'react-native';
import { useUtxUsersGetUserProfile } from '@utx/utx-client';
import { useVerifyOtp } from '@/features/bill-payment/hooks/useTfaVerify.hook';
import { usePayout } from '@/features/account-management/screens/details/hooks/usePayouts.hook';
import { CreatePayoutBodyDestination, CreatePayoutBodyPurpose, CreatePayoutBodySourceOfFunds } from '@utx/utx-access-client';

export interface TWOFASmsNativeScreenProps {
  onBack?: (flag?: boolean) => void;
  wallet?: any;
  calcFee?: any;
  beneficiaryInformation?: any;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const TWOFASmsNativeScreen: React.FC<TWOFASmsNativeScreenProps> = (props) => {
  const {
    onBack,
    wallet,
    calcFee,
    beneficiaryInformation,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('global-bill-payment');
  const layout = useContext(LayoutContext);
  const navigationAny = useNavigation<any>();

  const { runSubmit } = useInstructions();
  const postInstructionsSubmit = (
    instructionId: string,
    amount: string,
    asset: string,
    purpose: string,
    remittancePurpose: string,
    fundSource: string,
    feeSnapshotId: string,
    accountId: string,
    destination: any,
    otp: string,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runSubmit(
      {
        intent: 'REMITTANCE',
        instructionId: instructionId,
        amount: amount,
        asset: asset,
        purpose: purpose,
        remittancePurpose: remittancePurpose,
        fundSource: fundSource,
        feeSnapshotId: feeSnapshotId,
        method: 'UNIONPAY',
        accountId: accountId,
        destination: { type: 'UNIONPAY', attributes: destination },
        otp: otp
      },
      onSuccess,
      onError
    );
  };

  const { runPayoutSubmit } = usePayout();
  const postPayoutSubmit = (
    destination: CreatePayoutBodyDestination,
    quoteId: string,
    purpose: CreatePayoutBodyPurpose,
    sourceOfFunds: CreatePayoutBodySourceOfFunds,
    accountId: string,
    remarks: string,
    sms_otp: string,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runPayoutSubmit(
      {
        method: 'UNIONPAY',
        destination: destination,
        quoteId: quoteId,
        purpose: purpose,
        sourceOfFunds: sourceOfFunds,
        accountId: accountId,
        remarks: remarks,
        sms_otp: sms_otp
      },
      onSuccess,
      onError
    );
  };

  const { theme } = useDripsyTheme();
  const { $4, $20 } = theme.space || ({} as any);
  const {
    container,
    body,
    viewRowButton,
    viewButton2,
    textReturn,
    viewDividerHorizontal,
    iconView,
    otpView,
    otpText,
    titleText,
    viewAlert
  } = sxStyles;

  const [sizeWidthButton, setSizeWithButton] = useState<number>(0);
  const [countdownTime, setCountdownTime] = useState<number>(15);
  const paddingVerticalContainer = 30;

  const {
    smsVerify: { postSendSmsVerificationApi }
  } = useVerifyOtp(calcFee?.id!);
  const queryProfile = useUtxUsersGetUserProfile({
    query: {
      enabled: false
    }
  });

  const [pageState, setPageState] = useState<{
    ready?: boolean;
    code?: string;
    errorMessage?: string;
    requestAgain?: boolean;
  }>({});

  const mobile = useMemo(() => {
    if (queryProfile.data && queryProfile.data.data) {
      return queryProfile.data.data.loginPhoneNumber;
    }
    return '';
  }, [queryProfile.data?.data]);

  const countryCode = useMemo(() => {
    if (queryProfile.data && queryProfile.data.data) {
      return queryProfile.data.data.loginCountryCode;
    }
    return 852;
  }, [queryProfile.data?.data]);

  const [methodDestination, setMethodDestination] = useState<string>('-');
  useEffect(() => {
    queryProfile.refetch();
    postSendSmsVerificationApi(
      (data) => {
        if (data && data.data) {
          setMethodDestination(data.data?.destination || '-');
        }
      },
      (error) => undefined
    );
  }, []);

  useEffect(() => {
    let number = countdownTime;
    let status = pageState?.requestAgain;
    if (number === 0) {
      status = false;
      setPageState({ ...pageState, requestAgain: false });
      setCountdownTime(15);
    }
    var timer: ReturnType<typeof setTimeout>;
    if (number > 0 && status) {
      timer = setTimeout(() => {
        --number;
        setCountdownTime(number);
      }, 1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [pageState?.requestAgain, countdownTime]);

  const sendRequestAgain = () => {
    setPageState({ ...pageState, requestAgain: true });
    postSendSmsVerificationApi(
      (data) => {
        if (data && data.data) {
          setMethodDestination(data.data?.destination || '-');
        }
      },
      (error) => undefined
    );
  };

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const onConfirm = (code?: string) => {
    setPageState({ ...pageState, errorMessage: '' });
    setAlertMessage('');
    let otpCode = code || (pageState && pageState.code ? pageState.code : '');
    if (!pageState.ready) {
      setPageState({ ...pageState, errorMessage: t('global-bill-payment::otp.error--otp-invalid') });
    } else {
      let destination = {};
      if (beneficiaryInformation?.account?.id) {
        destination = {
          transferPeerId: beneficiaryInformation?.account?.id,
          firstName: beneficiaryInformation?.account?.attributes?.receiverFirstName,
          firstNameCN: beneficiaryInformation?.account?.attributes?.receiverFirstNameCN,
          surname: beneficiaryInformation?.account?.attributes?.receiverSurname,
          surnameCN: beneficiaryInformation?.account?.attributes?.receiverSurnameCN,
          accountNumber: beneficiaryInformation?.account?.attributes?.receiverAccountNumber,
          address: beneficiaryInformation?.account?.attributes?.receiverAddress,
          label: beneficiaryInformation?.account?.label
        };
      } else {
        destination = {
          firstName: beneficiaryInformation?.account?.firstNameEnglish,
          firstNameCN: beneficiaryInformation?.account?.firstNameChinese,
          surname: beneficiaryInformation?.account?.lastNameEnglish,
          surnameCN: beneficiaryInformation?.account?.lastNameChinese,
          accountNumber: beneficiaryInformation?.account?.accountNumber,
          address: beneficiaryInformation?.account?.accountHolderAddress,
          saveTransferPeer: beneficiaryInformation?.account?.isSave,
          label: beneficiaryInformation?.account?.isSave ? beneficiaryInformation?.account?.label : undefined,
          transferPeerId: undefined
        };
      }

      setIsLoadingSubmit(true);
      if (unionType == UnionType.PAYMENT) {
        postInstructionsSubmit(
          calcFee?.id,
          calcFee?.amount?.toString(),
          calcFee?.feeCurrency,
          beneficiaryInformation?.purpose,
          beneficiaryInformation?.purpose,
          beneficiaryInformation?.fundSource,
          calcFee?.feeSnapshotId,
          wallet?.id,
          destination,
          otpCode,
          (data: any) => {
            setIsLoadingSubmit(false);
            let refId = data?.data?.refId;
            navigationAny.navigate(navigationNavigate, {
              screen: navigationScreen,
              params: { screen: UnionPayStackRoutes.Placed, onBack, wallet, calcFee, beneficiaryInformation, refId }
            });
          },
          (error: any) => {
            setIsLoadingSubmit(false);
            const data: any = error.response?.data;
            const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
            setPageState({ ...pageState, errorMessage: errorMessage });
          }
        );
      } else if (unionType == UnionType.ACCOUNT) {
        postPayoutSubmit(
          {
            accountNumber: beneficiaryInformation?.account?.accountNumber,
            firstName: beneficiaryInformation?.account?.firstNameEnglish,
            firstNameCN: beneficiaryInformation?.account?.firstNameChinese,
            surname: beneficiaryInformation?.account?.lastNameEnglish,
            surnameCN: beneficiaryInformation?.account?.lastNameChinese,
            address: beneficiaryInformation?.account?.accountHolderAddress
          },
          calcFee?.id,
          beneficiaryInformation?.purpose,
          beneficiaryInformation?.fundSource,
          wallet?.id,
          beneficiaryInformation?.remark,
          otpCode,
          (data: any) => {
            setIsLoadingSubmit(false);
            let refId = data?.data?.refId;
            navigationAny.navigate(navigationNavigate, {
              screen: navigationScreen,
              params: { screen: UnionPayStackRoutes.Placed, onBack, wallet, calcFee, beneficiaryInformation, refId, unionType, refresh }
            });
          },
          (error: any) => {
            setIsLoadingSubmit(false);
            const data: any = error.response?.data;
            const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
            setPageState({ ...pageState, errorMessage: errorMessage });
          }
        );
      }
    }
  };

  useEffect(() => {
    if (pageState && pageState.code && pageState.ready) {
      onConfirm(pageState.code);
    }
  }, [pageState?.code, pageState?.ready]);

  const onCancel = () => {
    onBack && onBack();
  };

  const onLayoutLocal = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setSizeWithButton(width);
  };

  return (
    <View sx={container}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { themeColor: '$white' } }}
              onPress={() => {
                onBack && onBack();
              }}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t('global-bill-payment::glossary.title--sms-authentication')}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}></View>
        </View>
      </PageHeaderModalNative>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={body}>
          <View sx={iconView}>
            <Icon name="sms2FA" height={$20} width={$20} />
          </View>
          <TextBody sx={titleText}>{t('log-in::sign-up.title--instructions-2fa')}</TextBody>
          <View sx={otpView}>
            <TextCaption semiBold sx={otpText}>
              {t('log-in::sign-up.title--Enter2FAVerificationCode')}
            </TextCaption>
            <UniOtpInput
              errorText={pageState && !isEmpty(pageState.errorMessage) ? pageState.errorMessage : undefined}
              maxLength={OtpInput.MAX_LENGTH}
              onValueChange={(code) => {
                setPageState({ ...pageState, code, ready: code.length === OtpInput.MAX_LENGTH });
              }}
            />
            <View sx={sxStyles.requestAgainView}>
              <TextCaption sx={sxStyles.secondaryColorText}>
                {t('log-in::sign-up.title--verifycation-code-sent-to-destination', { destination: methodDestination })}
              </TextCaption>
              <View sx={sxStyles.requestNewCodeView}>
                <TextCaption sx={sxStyles.secondaryColorText}>
                  {t('log-in::sign-up.text--can-not-receive')}{' '}
                  {!pageState?.requestAgain && (
                    <TextCaption sx={sxStyles.textRequestAgain} onPress={() => sendRequestAgain()}>
                      {t('log-in::sign-up.text--request-again')}
                    </TextCaption>
                  )}
                  {pageState?.requestAgain && (
                    <TextCaption sx={sxStyles.requestAgainText}>
                      {t('log-in::sign-up.text--request-new-one')} {countdownTime} {t('log-in::sign-up.text--second')}
                    </TextCaption>
                  )}
                </TextCaption>
              </View>
            </View>
            {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={viewAlert} />}
          </View>
        </View>
      </ScrollView>
      <View sx={viewRowButton} onLayout={onLayoutLocal}>
        <UniButton
          variant={ButtonVariant.outline1}
          labelSx={textReturn}
          sx={{ ...viewButton2, ...{ width: (sizeWidthButton - 16) / 2 } }}
          label={t('global-bill-payment::glossary.back')}
          onPress={onCancel}
        />
        <View sx={viewDividerHorizontal} />
        <UniButton
          sx={{ ...viewButton2, ...{ width: (sizeWidthButton - 16) / 2 } }}
          label={t('global-bill-payment::glossary.confirm')}
          onPress={() => onConfirm(pageState.code)}
          loading={isLoadingSubmit}
          disabled={isLoadingSubmit || !pageState.ready}
        />
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  body: {
    pt: '$7',
    px: '$7',
    zIndex: 2,
    alignItems: 'center'
  },
  iconView: {
    height: '$20',
    width: '$20',
    mb: '$2'
  },
  otpView: {
    mt: '$10'
  },
  titleText: {
    textAlign: 'center'
  },
  otpText: {
    mb: '$1'
  },
  viewRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: '$10',
    mb: '$7',
    mx: '$7'
  },
  viewButton2: {
    width: '$60',
    height: '$11'
  },
  textReturn: {
    color: '$textPrimary'
  },
  viewDividerHorizontal: {
    width: '$4'
  },
  requestAgainView: {
    alignItems: 'center',
    mt: '$3'
  },
  requestNewCodeView: {
    width: '77%',
    alignItems: 'center',
    mt: '$5'
  },
  requestAgainText: {
    ml: '$2',
    mt: '$5',
    textAlign: 'center',
    color: '$textSecondary'
  },
  secondaryColorText: {
    color: '#666666'
  },
  textRequestAgain: {
    color: '#DC9B32'
  },
  viewAlert: {
    mt: '$3'
  }
};
